import { MenuItem } from "@components";
import {
  BadgeDollarRegular,
  ChartMixedRegular,
  ClockLight,
  CoinsLight,
  CreditCardLight,
  DiagramProjectRegular,
  DotRegular,
  FileInvoiceLight,
  FileLinesLight,
  MegaphoneRegular,
  Package,
  UserLight,
} from "@icons";
import Tooltip from "@src/components/tooltip";
import RequirePermission from "@src/modules/member/components/require-permission";
import { PERMISSIONS } from "@src/modules/member/constants/permissions";
import { useOrganizationHasFeature } from "@src/modules/member/hooks/organizations";
import { useMemberPath } from "@src/modules/member/hooks/use-member-path";
import { FeatureSlug } from "@src/modules/member/models/organization";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  CLIENT_LIST_PATH,
  CONTRACT_LIST_PATH,
  DASHBOARD_PATH,
  EXPENSES_LIST_PATH,
  FORMS_LIST_PATH,
  INVOICE_LIST_PATH,
  PRODUCT_LIST_PATH,
  PROJECT_LIST_PATH,
  RECURRING_INVOICE_LIST_PATH,
  SURCHARGE_LIST_PATH,
  TAXES_LIST_PATH,
  TIME_ENTRIES_LIST_PATH,
  TRANSACTIONS_LIST_PATH,
} from "../paths";
import Organization from "./organization";

interface MenuItemComponentPropTypes {
  minimized: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

const Divider = () => (
  <div className="flex w-full my-1 h-0 border-b border-divider" />
);

const OrganizationMenuItem = (props: MenuItemComponentPropTypes) => {
  return <Organization minimized={props.minimized} />;
};

const DashboardMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <MenuItem
      to={path(DASHBOARD_PATH())}
      icon={<ChartMixedRegular width={16} />}
      label={t("member.main.dashboard.dashboard")}
      minimized={props.minimized}
      setMobileMenuOpen={props.setMobileMenuOpen}
    />
  );
};

const DashboardDivider = () => (
  <RequirePermission permission={PERMISSIONS.CLIENTS_VIEW_LIST}>
    <Divider />
  </RequirePermission>
);

const ClientsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.CLIENTS_VIEW_LIST}>
      <MenuItem
        to={path(CLIENT_LIST_PATH())}
        icon={<UserLight width={16} />}
        label={t("clients")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const AfterClientDivider = () => (
  <RequirePermission permission={PERMISSIONS.PROJECTS_VIEW_LIST}>
    <Divider />
  </RequirePermission>
);

const ProjectsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.PROJECTS_VIEW_LIST}>
      <MenuItem
        to={path(PROJECT_LIST_PATH())}
        icon={<DiagramProjectRegular width={16} />}
        label={t("member.main.projects.projectList")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const TimeEntriesMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.TIME_ENTRIES_VIEW_LIST}>
      <MenuItem
        to={path(TIME_ENTRIES_LIST_PATH())}
        icon={<ClockLight width={16} />}
        label={t("member.main.timeEntries.timeEntries")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const AfterTimeEntriesDivider = () => (
  <RequirePermission permission={PERMISSIONS.INVOICES_VIEW_LIST}>
    <Divider />
  </RequirePermission>
);

const InvoicesMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission
      any={[
        PERMISSIONS.INVOICES_VIEW_LIST,
        PERMISSIONS.RECURRING_INVOICES_VIEW_LIST,
      ]}
    >
      <MenuItem
        submenu={[
          {
            key: "invoices",
            component: () => (
              <RequirePermission permission={PERMISSIONS.INVOICES_VIEW_LIST}>
                <MenuItem
                  to={path(INVOICE_LIST_PATH())}
                  icon={<DotRegular width={13} />}
                  label={t("member.main.invoices.invoices")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              </RequirePermission>
            ),
          },
          {
            key: "recurring-invoices",
            component: () => (
              <RequirePermission
                permission={PERMISSIONS.RECURRING_INVOICES_VIEW_LIST}
              >
                <MenuItem
                  to={path(RECURRING_INVOICE_LIST_PATH())}
                  icon={<DotRegular width={13} />}
                  label={t("member.main.invoices.recurring")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              </RequirePermission>
            ),
          },
        ]}
        icon={<FileInvoiceLight width={13} />}
        label={t("invoices")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const TransactionsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.TRANSACTIONS_VIEW_LIST}>
      <MenuItem
        to={path(TRANSACTIONS_LIST_PATH())}
        icon={<CreditCardLight width={16} />}
        label={t("transactions")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const ContractsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();
  const hasContracts = useOrganizationHasFeature(FeatureSlug.Contracts);

  if (!hasContracts) return null;

  return (
    <RequirePermission permission={PERMISSIONS.CONTRACTS_VIEW_LIST}>
      <MenuItem
        to={path(CONTRACT_LIST_PATH())}
        icon={<FileLinesLight width={16} />}
        label={t("member.main.contracts.contracts")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const ProductsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();
  const hasContracts = useOrganizationHasFeature(FeatureSlug.Contracts);

  if (!hasContracts) return null;

  return (
    <RequirePermission permission={PERMISSIONS.PRODUCTS_VIEW}>
      <MenuItem
        to={path(PRODUCT_LIST_PATH())}
        icon={<Package width={16} />}
        label={t("member.main.products.products")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const FeesMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission
      any={[PERMISSIONS.TAXES_VIEW_LIST, PERMISSIONS.SURCHARGES_VIEW_LIST]}
    >
      <MenuItem
        submenu={[
          {
            key: "taxes",
            component: () => (
              <RequirePermission permission={PERMISSIONS.TAXES_VIEW_LIST}>
                <MenuItem
                  to={path(TAXES_LIST_PATH())}
                  icon={<DotRegular width={13} />}
                  label={t("taxes")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              </RequirePermission>
            ),
          },
          {
            key: "surcharges",
            component: () => (
              <RequirePermission permission={PERMISSIONS.SURCHARGES_VIEW_LIST}>
                <MenuItem
                  to={path(SURCHARGE_LIST_PATH())}
                  icon={<DotRegular width={13} />}
                  label={t("surcharges")}
                  minimized={props.minimized}
                  setMobileMenuOpen={props.setMobileMenuOpen}
                />
              </RequirePermission>
            ),
          },
        ]}
        icon={<CoinsLight />}
        label={t("feesAndTaxes")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const AfterFeesDivider = () => (
  <RequirePermission permission={PERMISSIONS.FORMS_VIEW_LIST}>
    <Divider />
  </RequirePermission>
);

const ExpensesMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.EXPENSES_VIEW}>
      <MenuItem
        to={path(EXPENSES_LIST_PATH())}
        icon={<BadgeDollarRegular width={16} />}
        label={t("member.main.expenses.expenses")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const AfterExpensesDivider = () => (
  <RequirePermission permission={PERMISSIONS.EXPENSES_VIEW}>
    <Divider />
  </RequirePermission>
);

const FormsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();
  const { path } = useMemberPath();

  return (
    <RequirePermission permission={PERMISSIONS.FORMS_VIEW_LIST}>
      <MenuItem
        to={path(FORMS_LIST_PATH())}
        icon={<FileLinesLight width={16} />}
        label={t("member.main.forms.forms")}
        minimized={props.minimized}
        setMobileMenuOpen={props.setMobileMenuOpen}
      />
    </RequirePermission>
  );
};

const ChangeLogsMenuItem = (props: MenuItemComponentPropTypes) => {
  const { t } = useTranslation();

  return (
    <button
      data-canny-changelog
      className={classNames(
        "flex rounded items-center justify-center hover:bg-page hover:text-primary",
        {
          "w-target h-target mx-auto": props.minimized,
          "w-full min-h-target": !props.minimized,
        },
      )}
      aria-label={t("member.main.viewChangeLogs")}
    >
      <Tooltip label={t("member.main.viewChangeLogs")}>
        {props.minimized && <MegaphoneRegular width={20} />}
      </Tooltip>
      {!props.minimized && (
        <span className="ml-2 text-sm">{t("member.main.viewChangeLogs")}</span>
      )}
    </button>
  );
};

export const memberMainMenuList = [
  { key: "organization-menu-item", component: OrganizationMenuItem },
  { key: "dashboard", component: DashboardMenuItem },
  { key: "dashboard-divider", component: DashboardDivider },
  { key: "client", component: ClientsMenuItem },
  { key: "after-client-divider", component: AfterClientDivider },
  { key: "projects", component: ProjectsMenuItem },
  { key: "time-entries", component: TimeEntriesMenuItem },
  { key: "after-time-entries-divider", component: AfterTimeEntriesDivider },
  { key: "invoices-menu", component: InvoicesMenuItem },
  { key: "transactions", component: TransactionsMenuItem },
  { key: "contracts", component: ContractsMenuItem },
  { key: "products", component: ProductsMenuItem },
  { key: "fees", component: FeesMenuItem },
  { key: "after-fees-divider", component: AfterFeesDivider },
  { key: "expenses", component: ExpensesMenuItem },
  { key: "after-expenses-divider", component: AfterExpensesDivider },
  { key: "forms", component: FormsMenuItem },
];

export const memberExtraMenuList = [
  { key: "change-logs", component: ChangeLogsMenuItem },
];
